import { Injectable, NgZone } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';

/**
 * In-memory pub/sub for UiMessage events streamed in over SSE. Components
 * subscribe via {@link on}; the SSE layer (UiMessageService) calls
 * {@link publish} when a message arrives.
 *
 * Mirrors bookwise-ui's UiMessageBus exactly — same shape across tenants
 * is intentional so a dev who knows one knows all.
 */
@Injectable({ providedIn: 'root' })
export class UiMessageBus {

    private subjects = new Map<string, Subject<any>>();
    private connectedSubject = new BehaviorSubject<boolean>(false);
    readonly connected$ = this.connectedSubject.asObservable();

    constructor(private zone: NgZone) {}

    setConnected(connected: boolean): void {
        this.zone.run(() => this.connectedSubject.next(connected));
    }

    /** Called by the SSE layer when a typed event arrives. */
    publish<T>(type: string, payload: T): void {
        let subject = this.subjects.get(type);
        if (!subject) {
            subject = new Subject<T>();
            this.subjects.set(type, subject);
        }
        // Always re-enter Angular zone so subscribers' change detection fires.
        this.zone.run(() => subject!.next(payload));
    }

    /**
     * Subscribe to a typed event. Returns an Observable that emits each time
     * a message of {@code type} is received. Components should
     * {@code .takeUntil(destroy$)} or unsubscribe in {@code ngOnDestroy}.
     */
    on<T>(type: string): Observable<T> {
        let subject = this.subjects.get(type);
        if (!subject) {
            subject = new Subject<T>();
            this.subjects.set(type, subject);
        }
        return subject.asObservable();
    }

    /** Optional: complete + drop a subject. Rarely needed. */
    clear(type: string): void {
        this.subjects.get(type)?.complete();
        this.subjects.delete(type);
    }
}
